import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

// Services & Types
import { FundService } from "services/fund.service";
import { ISaveFund } from "models/church.types";

export default function FundRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string }>({});

  // Initialisation conforme à l'interface ISaveFund
  const [fund, setFund] = useState<ISaveFund>({
    name: "",
    description: "",
    official: false,
    active: true,
  });

  useEffect(() => {
    const fetchFund = async () => {
      if (isEditMode && id) {
        try {
          const data = await FundService.getById(parseInt(id));
          setFund({
            name: data.name,
            description: data.description || "",
            official: data.official,
            active: data.active,
          });
        } catch (error) {
          toast.error("Erreur lors du chargement de la caisse");
          history.push("/admin/funds");
        }
      }
    };
    fetchFund();
  }, [id, isEditMode, history]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: { name?: string } = {};
    if (!fund.name.trim()) {
      newErrors.name = "Le nom de la caisse est requis";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        await FundService.update({ ...fund, id: parseInt(id) });
        toast.success("Caisse mise à jour avec succès");
      } else {
        await FundService.create(fund);
        toast.success("Caisse enregistrée avec succès");
      }
      history.push("/admin/funds");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement de la caisse.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen mt-6 pt-24 items-center justify-start px-4">
      <div className="w-full max-w-2xl">
        <div className="bg-[#1E293B] border border-slate-700 shadow-2xl rounded-xl overflow-hidden">
          <div className="bg-[#0F172A] px-6 py-4 border-b border-slate-700 flex justify-between items-center">
            <h6 className="text-white text-sm font-bold uppercase tracking-widest">
              <i className="fas fa-vault mr-2 text-[#D4AF37]"></i>
              {isEditMode ? "Éditer la caisse" : "Nouvelle caisse / fonds"}
            </h6>
          </div>

          <form
            onSubmit={handleSubmit}
            className="px-6 py-8 grid grid-cols-1 gap-6"
          >
            {/* Nom de la caisse */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Nom de la caisse *
              </label>
              <input
                type="text"
                value={fund.name}
                onChange={(e) => {
                  setFund({ ...fund, name: e.target.value });
                  if (errors.name) setErrors({});
                }}
                placeholder="Ex: Caisse Principale, Fonds de secours..."
                className={`w-full bg-[#0F172A] border ${
                  errors.name ? "border-red-500" : "border-slate-700"
                } rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all`}
              />
              {errors.name && (
                <span className="text-red-500 text-xs mt-1 ml-1 block">
                  {errors.name}
                </span>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Description
              </label>
              <textarea
                value={fund.description || ""}
                onChange={(e) =>
                  setFund({ ...fund, description: e.target.value })
                }
                placeholder="Détails sur l'utilisation ou l'affectation de cette caisse..."
                className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none h-24 transition-all resize-none"
              />
            </div>

            {/* Options (Official & Active) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Caisse Officielle */}
              <div className="bg-[#0F172A] p-4 rounded-lg border border-slate-700/50 flex items-center justify-between">
                <div>
                  <span className="text-xs text-white font-bold block">
                    Caisse Officielle
                  </span>
                  <span className="text-[10px] text-slate-400">
                    Rattachée au budget de l'église
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setFund({ ...fund, official: !fund.official })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    fund.official ? "bg-[#D4AF37]" : "bg-slate-700"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-[#0F172A] transition-transform ${
                      fund.official ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {/* Caisse Active */}
              <div className="bg-[#0F172A] p-4 rounded-lg border border-slate-700/50 flex items-center justify-between">
                <div>
                  <span className="text-xs text-white font-bold block font-sans">
                    Statut
                  </span>
                  <span className="text-[10px] text-slate-400">
                    Active et prête pour les transactions
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setFund({ ...fund, active: !fund.active })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    fund.active ? "bg-emerald-500" : "bg-slate-700"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-[#0F172A] transition-transform ${
                      fund.active ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="mt-6 flex justify-end gap-4 border-t border-slate-700 pt-6">
              <button
                type="button"
                onClick={() => history.push("/admin/funds")}
                className="px-6 py-3 text-slate-400 hover:text-white font-bold text-xs uppercase transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase hover:scale-105 active:scale-95 transition-all shadow-md disabled:opacity-50"
              >
                {loading ? "Chargement..." : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
